import type { LicenceRuntime, CloudWebEntitlementClaims } from '@infra/licence/LicenceRuntime';
import { cloudLicenceMessage } from '@application/licence/CloudLicenceMessages';
import { verifyServerToken } from '@infra/licence/ServerTokenVerifier';
import type { RemoteInstallationInfo } from '@infra/api/BusinessApiClient';
import { logger } from '@infra/telemetry/Logger';

/** « Empreinte » d'un navigateur Cloud : le jeton `entitlements` web est émis pour ce poste logique. */
export const CLOUD_WEB_DEVICE_FINGERPRINT = 'cloud-web';

type Listener = (jwt: string, expectedTenantId: string | undefined) => Promise<boolean>;
type InstallationListener = (info: RemoteInstallationInfo, expectedTenantId: string) => boolean;

let listener: Listener | null = null;
let installationListener: InstallationListener | null = null;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isRoleViews(value: unknown): value is Record<string, string[]> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(isStringArray)
  );
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : 0;
}

/**
 * Vérifie un jeton `entitlements` reçu après authentification du navigateur Cloud et applique
 * les droits réels. Refus (retourne false, droits inchangés) si signature, type, empreinte,
 * tenant ou structure sont invalides. Aucune valeur n'est lue d'un jeton non vérifié.
 */
export async function applyCloudWebEntitlementsJwt(
  runtime: LicenceRuntime,
  jwt: unknown,
  expectedTenantId?: string,
  keyResolver?: (kid: string | undefined) => string | null,
): Promise<boolean> {
  const verified = await verifyServerToken(jwt, 'entitlements', {
    deviceFingerprint: CLOUD_WEB_DEVICE_FINGERPRINT,
    ...(keyResolver ? { keyResolver } : {}),
  });
  if (!verified.ok) {
    logger.warn({ event: 'cloud_web_entitlements_rejected', reason: verified.reason });
    return false;
  }
  const p = verified.payload;
  if (typeof p.tenant_id !== 'string' || !p.tenant_id) return false;
  if (expectedTenantId && p.tenant_id !== expectedTenantId) {
    logger.warn({ event: 'cloud_web_entitlements_rejected', reason: 'tenant_incorrect' });
    return false;
  }
  if (!isStringArray(p.features) || typeof p.tier !== 'string') return false;
  const claims: CloudWebEntitlementClaims = {
    sub: typeof p.sub === 'string' ? p.sub : 'cloud-web',
    tenant_id: p.tenant_id,
    features: p.features,
    tier: p.tier,
    max_salaries: count(p.max_salaries),
    max_users: count(p.max_users),
    max_devices: count(p.max_devices),
    role_views: isRoleViews(p.role_views) ? p.role_views : {},
    iat: typeof p.iat === 'number' ? p.iat : 0,
    exp: typeof p.exp === 'number' ? p.exp : 0,
  };
  runtime.applyCloudWebEntitlements(claims);
  return true;
}

/** Enregistré au démarrage Cloud navigateur : reçoit les jetons livrés par l'API après connexion. */
export function registerCloudWebEntitlementsListener(fn: Listener | null): void {
  listener = fn;
}

/** Appelé par la couche d'authentification quand la réponse serveur porte un `entitlements_jwt`. */
export async function publishCloudWebEntitlements(
  jwt: unknown,
  expectedTenantId?: string,
): Promise<boolean> {
  if (!listener || typeof jwt !== 'string') return false;
  return listener(jwt, expectedTenantId);
}

const STATUS_TO_RESTRICTION: Record<
  string,
  'license_revoked' | 'license_suspended' | 'license_expired'
> = {
  revoked: 'license_revoked',
  suspended: 'license_suspended',
  expired: 'license_expired',
};

/**
 * Applique les droits de GET /v1/auth/me (session authentifiée : features, tier, plafonds, statut)
 * à la session Cloud navigateur. Refus fermé : sans liste de modules ni bon tenant, aucun droit.
 * Statut révoqué / suspendu / expiré : lecture seule avec message explicite.
 */
export function applyCloudWebInstallation(
  runtime: LicenceRuntime,
  info: RemoteInstallationInfo,
  expectedTenantId: string,
): boolean {
  if (!info || info.tenant_id !== expectedTenantId) return false;
  const list = isStringArray(info.features)
    ? info.features
    : isStringArray(info.modules)
      ? info.modules
      : null;
  if (!list) return false;
  runtime.applyCloudWebEntitlements({
    sub: 'cloud-web',
    tenant_id: info.tenant_id,
    features: list,
    tier: typeof info.tier === 'string' ? info.tier : 'starter',
    max_salaries: count(info.max_salaries),
    max_users: count(info.max_users),
    max_devices: 0,
    role_views: runtime.getState().claims?.role_views ?? {},
    iat: 0,
    exp: info.license_expires_at ? Math.floor(Date.parse(info.license_expires_at) / 1000) || 0 : 0,
  });
  const refusal = info.license_status ? STATUS_TO_RESTRICTION[info.license_status] : undefined;
  if (refusal) {
    runtime.setRestriction({
      kind: refusal === 'license_revoked' ? 'revoked' : 'suspended',
      message: cloudLicenceMessage(refusal) ?? '',
      since: new Date().toISOString(),
    });
  } else if (runtime.getRestriction()?.kind !== 'offline_too_long') {
    runtime.setRestriction(null);
  }
  return true;
}

export function registerCloudWebInstallationListener(fn: InstallationListener | null): void {
  installationListener = fn;
}

/** Appelé par la couche d'authentification avec le bloc `installation` de /v1/auth/me. */
export function publishCloudWebInstallation(
  info: RemoteInstallationInfo | undefined,
  expectedTenantId: string,
): boolean {
  if (!info || !installationListener) return false;
  return installationListener(info, expectedTenantId);
}
